import React, { useState } from "react";
import { Button, Flex, Icon, Text } from "@chakra-ui/react";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";
import TransactionsHeader from "./TransactionsHeader";
import TransactionsTable from "./TransactionsTable";
import ApproveTransactionDialog from "./ApproveTransactionDialog";
import usePendingTransactions from "./hooks/usePendingTransactions";
import { usePendingTransactionsBadge } from "contexts/PendingTransactionsBadgeContext";

/**
 * Cursor-style pagination: previous/next buttons + current page indicator.
 * Total is unknown server-side so we infer hasNext from the page-size flag.
 * @param {Object} props - page (1-indexed), hasMore (boolean), onChange (fn)
 * @returns {React.ReactElement|null}
 */
function PaginationRow({ page, hasMore, onChange }) {
  if (page <= 1 && !hasMore) return null;
  return (
    <Flex justify="center" align="center" gap="10px" py="4px">
      <Button size="sm" variant="outline" colorScheme="brand" isDisabled={page <= 1}
        leftIcon={<Icon as={MdChevronLeft} />} onClick={() => onChange(Math.max(1, page - 1))}>
        Précédent
      </Button>
      <Text fontSize="sm" color="secondaryGray.600" _dark={{ color: "secondaryGray.400" }} px="8px">Page {page}</Text>
      <Button size="sm" variant="outline" colorScheme="brand" isDisabled={!hasMore}
        rightIcon={<Icon as={MdChevronRight} />} onClick={() => onChange(page + 1)}>
        Suivant
      </Button>
    </Flex>
  );
}

/**
 * Admin page listing all pending transactions awaiting approval.
 * Each row exposes an Approve button that opens a confirmation modal.
 * Successful approval refreshes both the page list and the sidebar badge.
 * @returns {React.ReactElement}
 */
function Transactions() {
  const [page, setPage] = useState(1);
  const [toApprove, setToApprove] = useState(null);
  const { items, hasMore, loading, error, refresh } = usePendingTransactions(page);
  const { refresh: refreshBadge } = usePendingTransactionsBadge();

  const handleApproved = () => { refresh(); refreshBadge(); };

  return (
    <Flex direction="column" gap="20px">
      <TransactionsHeader count={items.length} hasMore={hasMore} />
      <TransactionsTable items={items} loading={loading} error={error} onApprove={setToApprove} />
      <PaginationRow page={page} hasMore={hasMore} onChange={setPage} />
      <ApproveTransactionDialog transaction={toApprove}
        onClose={() => setToApprove(null)} onApproved={handleApproved} />
    </Flex>
  );
}

export default Transactions;
